"use client";

import React from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  Box,
  useToast,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  ButtonGroup,
} from "@chakra-ui/react";
import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  agentService,
  type RetellLLM,
  type UpdateRetellLLMParams,
  type VoiceDetails,
} from "../../voice-services/agentService";

const REALTIME_SUPPORTED_VOICES = [
  "openai-Adrian",
  "openai-Alloy",
  "openai-Amy",
  "openai-Andrew",
  "openai-Anna",
  "openai-Anthony",
  "openai-Ash",
  "openai-Ballad",
  "openai-Brian",
  "openai-Carola",
  "openai-Cedar",
  "openai-Chloe",
  "openai-Cimo",
  "openai-Coral",
  "openai-Echo",
  "openai-Emily",
  "openai-Grace",
  "openai-Julia",
  "openai-Kate",
  "openai-Kathrine",
  "openai-Marin",
  "openai-Marissa",
  "openai-Monika",
  "openai-Myra",
  "openai-Paola",
  "openai-Sage",
  "openai-Santiago",
  "openai-Shimmer",
  "openai-Susan",
  "openai-Verse",
  "openai-Zuri",
];

interface ModelSelectProps {
  initialLLM: RetellLLM | null;
  agentId: string;
  setSelectedLLM: React.Dispatch<React.SetStateAction<RetellLLM | null>>;
  currentVoice?: VoiceDetails | null;
  onVoiceChange?: (voiceId: string) => Promise<void>;
}

const modelOptions = [
  // S2S Models (Realtime)
  { name: "GPT 4o Realtime", value: "gpt-4o-realtime", price: "$0.5/min" },
  {
    name: "GPT 4o mini Realtime",
    value: "gpt-4o-mini-realtime",
    price: "$0.125/min",
  },
  { name: "GPT Realtime", value: "gpt-realtime", price: "$0.4/min" },
  { name: "GPT Realtime Mini", value: "gpt-realtime-mini", price: "$0.2/min" },

  // Text Models - GPT 5 Series
  { name: "GPT 5", value: "gpt-5", price: "$0.04/min" },
  { name: "GPT 5.1", value: "gpt-5.1", price: "$0.04/min" },
  { name: "GPT 5.2", value: "gpt-5.2", price: "$0.04/min" },
  { name: "GPT 5 Mini", value: "gpt-5-mini", price: "$0.012/min" },
  { name: "GPT 5 Nano", value: "gpt-5-nano", price: "$0.003/min" },

  // Text Models - GPT 4.1 Series
  { name: "GPT 4.1", value: "gpt-4.1", price: "$0.045/min" },
  { name: "GPT 4.1 Mini", value: "gpt-4.1-mini", price: "$0.016/min" },
  { name: "GPT 4.1 Nano", value: "gpt-4.1-nano", price: "$0.004/min" },

  // Text Models - Claude Series
  { name: "Claude 4.5 Sonnet", value: "claude-4.5-sonnet", price: "$0.06/min" },
  { name: "Claude 4.5 Haiku", value: "claude-4.5-haiku", price: "$0.02/min" },

  // Text Models - Gemini
  { name: "Gemini 2.5 Flash", value: "gemini-2.5-flash", price: "$0.035/min" },
  {
    name: "Gemini 2.5 Flash Lite",
    value: "gemini-2.5-flash-lite",
    price: "$0.006/min",
  },
  { name: "Gemini 3.0 Flash", value: "gemini-3.0-flash", price: "$0.01/min" },
];

export const ModelSelect: React.FC<ModelSelectProps> = React.memo(
  ({ initialLLM, agentId, setSelectedLLM, currentVoice, onVoiceChange }) => {
    const [selectedLLM, setSelectedLLMState] = React.useState<RetellLLM | null>(
      initialLLM,
    );
    const [temperature, setTemperature] = React.useState(
      () => initialLLM?.model_temperature ?? 0.7,
    );
    const [tempTemperature, setTempTemperature] = React.useState(temperature);
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const toast = useToast();

    React.useEffect(() => {
      setSelectedLLMState(initialLLM);
    }, [initialLLM]);

    React.useEffect(() => {
      if (selectedLLM)
        localStorage.setItem("selectedLLM", JSON.stringify(selectedLLM));
    }, [selectedLLM]);
    React.useEffect(() => {
      localStorage.setItem("llmTemperature", String(temperature));
    }, [temperature]);

    const isRealtime = (value: string) => value.includes("realtime");

    const isVoiceCompatibleWithRealtime = (
      voice: VoiceDetails | null | undefined,
    ): boolean => {
      if (!voice) return false;
      return REALTIME_SUPPORTED_VOICES.includes(voice.voice_id);
    };

    const buildUpdatePayloadForModelChange = React.useCallback(
      (modelName: string): UpdateRetellLLMParams => {
        // Retell requires start_speaker; reuse current if present.
        const start_speaker = (selectedLLM?.start_speaker || "agent") as
          | "user"
          | "agent";

        const params: UpdateRetellLLMParams = {
          start_speaker,
          model_temperature: temperature,
        };

        // Ensure only one of model / s2s_model is set
        if (isRealtime(modelName)) {
          params.s2s_model = modelName as
            | "gpt-4o-realtime"
            | "gpt-4o-mini-realtime"
            | "gpt-realtime"
            | "gpt-realtime-mini"
            | null;
          params.model = null;
        } else {
          params.model = modelName as
            | "gpt-4.1"
            | "gpt-4.1-mini"
            | "gpt-4.1-nano"
            | "gpt-5"
            | "gpt-5.1"
            | "gpt-5.2"
            | "gpt-5-mini"
            | "gpt-5-nano"
            | "claude-4.5-sonnet"
            | "claude-4.5-haiku"
            | "gemini-2.5-flash"
            | "gemini-2.5-flash-lite"
            | "gemini-3.0-flash"
            | null;
          params.s2s_model = null;
        }

        return params;
      },
      [isRealtime, selectedLLM?.start_speaker, temperature],
    );

    const handleModelChange = React.useCallback(
      async (modelName: string) => {
        if (!selectedLLM) {
          toast({
            title: "LLM not loaded",
            description:
              "Please wait for the LLM to load before changing model.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Enforce realtime voice compatibility
        if (
          isRealtime(modelName) &&
          !isVoiceCompatibleWithRealtime(currentVoice)
        ) {
          const defaultVoice = "openai-Alloy";

          if (onVoiceChange) {
            try {
              await onVoiceChange(defaultVoice);
              toast({
                title: "Voice automatically updated",
                description: `Switched to ${defaultVoice.replace(
                  "openai-",
                  "",
                )} (compatible with realtime models)`,
                status: "info",
                duration: 4000,
                isClosable: true,
              });
            } catch {
              toast({
                title: "Unable to switch voice",
                description:
                  "Please manually select an OpenAI voice before using realtime models.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
              return;
            }
          } else {
            toast({
              title: "Incompatible Voice",
              description:
                "Realtime models only support OpenAI voices. Please select an OpenAI voice first before switching to a realtime model.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }
        }

        try {
          // ✅ Update the SAME LLM (keeps tools + KB + states intact)
          const updateParams = buildUpdatePayloadForModelChange(modelName);

          const updatedLLM = await agentService.updateRetellLLM(
            selectedLLM.llm_id,
            updateParams,
          );

          setSelectedLLMState(updatedLLM);
          setSelectedLLM(updatedLLM);
          localStorage.setItem("selectedLLM", JSON.stringify(updatedLLM));

          toast({
            title: "Model updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          toast({
            title: "Error updating model",
            description:
              error?.response?.data?.message ||
              error?.message ||
              "An unexpected error occurred",
            status: "error",
            duration: 3000,
          });
        }
      },
      [
        selectedLLM,
        currentVoice,
        onVoiceChange,
        toast,
        isRealtime,
        buildUpdatePayloadForModelChange,
        setSelectedLLM,
      ],
    );

    const handleTemperatureSave = React.useCallback(async () => {
      try {
        if (!selectedLLM) return;

        // ✅ Update SAME LLM temperature (keeps tools + KB + states intact)
        const params: UpdateRetellLLMParams = {
          start_speaker: (selectedLLM.start_speaker || "agent") as
            | "user"
            | "agent",
          model_temperature: tempTemperature,
          // Do NOT touch model / s2s_model here; keep as-is
        };

        const updatedLLM = await agentService.updateRetellLLM(
          selectedLLM.llm_id,
          params,
        );

        setTemperature(tempTemperature);
        setIsSettingsOpen(false);
        setSelectedLLMState(updatedLLM);
        setSelectedLLM(updatedLLM);
        localStorage.setItem("selectedLLM", JSON.stringify(updatedLLM));

        toast({
          title: "Temperature updated successfully",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        toast({
          title: "Error updating temperature",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    }, [selectedLLM, tempTemperature, agentId, toast, setSelectedLLM]);

    const handleSettingsCancel = () => {
      setTempTemperature(temperature);
      setIsSettingsOpen(false);
    };

    const getModelIcon = React.useCallback((modelName: string) => {
      if (modelName.includes("gpt")) {
        return (
          <Box as="span" w="20px" h="20px">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 4C0 1.79086 1.79086 0 4 0H16C18.2091 0 20 1.79086 20 4V16C20 18.2091 18.2091 20 16 20H4C1.79086 20 0 18.2091 0 16V4Z"
                fill="white"
              />
              <path d="M17.1673 9.73195C17.3321 10.0996 17.4367 10.4927 17.4795 10.8937C17.5207 11.2947 17.5001 11.7004 17.4145 12.095C17.3305 12.4897 17.1847 12.8685 16.9819 13.2171C16.8487 13.4501 16.6918 13.6688 16.5111 13.8701C16.332 14.0698 16.1323 14.2505 15.9152 14.409C15.6965 14.5675 15.4635 14.7006 15.2163 14.81C14.9706 14.9177 14.7139 15.0002 14.4508 15.054C14.3272 15.4376 14.1433 15.8005 13.9056 16.127C13.6694 16.4535 13.3826 16.7404 13.0561 16.9765C12.7296 17.2143 12.3682 17.3981 11.9847 17.5217C11.6011 17.6469 11.2001 17.7087 10.796 17.7087C10.5282 17.7103 10.2587 17.6818 9.99563 17.6279C9.73412 17.5724 9.47736 17.4884 9.23171 17.3791C8.98605 17.2697 8.75307 17.1334 8.53594 16.9749C8.32039 16.8164 8.12069 16.6342 7.94318 16.4329C7.54696 16.5185 7.14123 16.5391 6.74025 16.4979C6.33927 16.4551 5.94621 16.3505 5.57693 16.1857C5.20924 16.0224 4.86848 15.8005 4.56894 15.5295C4.26939 15.2585 4.01422 14.9415 3.81294 14.5928C3.67822 14.3599 3.56728 14.1142 3.48328 13.859C3.39928 13.6039 3.34381 13.3408 3.31528 13.0729C3.28676 12.8067 3.28834 12.5372 3.31687 12.2694C3.3454 12.0031 3.40404 11.74 3.48804 11.4849C3.2186 11.1853 2.99672 10.8446 2.83189 10.4769C2.66865 10.1076 2.56246 9.7161 2.52125 9.31513C2.47846 8.91415 2.50065 8.50841 2.58465 8.11377C2.66865 7.71913 2.81446 7.34034 3.01732 6.99167C3.15045 6.75869 3.30736 6.53838 3.48645 6.33869C3.66555 6.13899 3.86683 5.95831 4.08396 5.79982C4.30109 5.64133 4.53565 5.50662 4.78131 5.39884C5.02856 5.28949 5.28531 5.20866 5.5484 5.15477C5.67203 4.76964 5.85587 4.40828 6.09202 4.08179C6.32976 3.7553 6.61662 3.46844 6.94311 3.2307C7.2696 2.99455 7.63096 2.81071 8.0145 2.6855C8.39805 2.56188 8.79903 2.49848 9.20318 2.50007C9.47102 2.49848 9.74046 2.52542 10.0036 2.5809C10.2666 2.63637 10.5234 2.71878 10.7691 2.82814C11.0147 2.93908 11.2477 3.0738 11.4648 3.23229C11.682 3.39236 11.8817 3.57304 12.0592 3.77432C12.4538 3.69032 12.8595 3.66972 13.2605 3.71093C13.6615 3.75214 14.053 3.85832 14.4222 4.02157C14.7899 4.1864 15.1307 4.4067 15.4302 4.67772C15.7298 4.94715 15.985 5.26254 16.1862 5.6128C16.321 5.8442 16.4319 6.08986 16.5159 6.34661C16.5999 6.60178 16.6569 6.86487 16.6839 7.13272C16.7124 7.40057 16.7124 7.67 16.6823 7.93785C16.6538 8.2057 16.5951 8.46879 16.5111 8.72396C16.7822 9.0235 17.0025 9.36267 17.1673 9.73195ZM11.8864 16.4979C12.2319 16.3552 12.5457 16.1444 12.8104 15.8798C13.0751 15.6151 13.2859 15.3013 13.4285 14.9542C13.5712 14.6087 13.6456 14.2378 13.6456 13.8638V10.3295C13.6446 10.3263 13.6435 10.3226 13.6425 10.3184C13.6414 10.3152 13.6398 10.312 13.6377 10.3089C13.6356 10.3057 13.633 10.303 13.6298 10.3009C13.6266 10.2978 13.6235 10.2956 13.6203 10.2946L12.3413 9.55603V13.8257C12.3413 13.8685 12.3349 13.9129 12.3238 13.9541C12.3127 13.9969 12.2969 14.0365 12.2747 14.0746C12.2525 14.1126 12.2272 14.1475 12.1955 14.1776C12.1647 14.2083 12.13 14.2349 12.0924 14.2568L9.06371 16.005C9.03835 16.0208 8.99556 16.043 8.97337 16.0557C9.09857 16.1619 9.23488 16.2554 9.37752 16.3378C9.52174 16.4202 9.67072 16.49 9.82604 16.547C9.98136 16.6025 10.1414 16.6453 10.3031 16.6738C10.4663 16.7023 10.6312 16.7166 10.796 16.7166C11.17 16.7166 11.5409 16.6421 11.8864 16.4979ZM4.67354 14.0983C4.86214 14.4233 5.11097 14.7054 5.40735 14.9336C5.70531 15.1618 6.04289 15.3282 6.40425 15.4249C6.7656 15.5216 7.14281 15.5469 7.51368 15.4978C7.88454 15.4487 8.24114 15.3282 8.56605 15.1412L11.6281 13.374L11.636 13.3661C11.6381 13.364 11.6397 13.3608 11.6407 13.3566C11.6429 13.3534 11.6444 13.3503 11.6455 13.3471V11.8573L7.94952 13.9953C7.91149 14.0175 7.87186 14.0334 7.83066 14.046C7.78786 14.0571 7.74507 14.0619 7.70069 14.0619C7.6579 14.0619 7.61511 14.0571 7.57232 14.046C7.53111 14.0334 7.4899 14.0175 7.45187 13.9953L4.42313 12.2456C4.39618 12.2298 4.35656 12.206 4.33437 12.1917C4.30584 12.355 4.29158 12.5198 4.29158 12.6846C4.29158 12.8494 4.30743 13.0143 4.33596 13.1775C4.36449 13.3392 4.40886 13.4993 4.46433 13.6546C4.52139 13.8099 4.59113 13.9589 4.67354 14.1015V14.0983ZM3.87792 7.48932C3.6909 7.81423 3.57045 8.17241 3.52132 8.54328C3.47219 8.91415 3.49755 9.28977 3.59423 9.65271C3.6909 10.0141 3.85732 10.3516 4.08554 10.6496C4.31377 10.946 4.59747 11.1948 4.92078 11.3818L7.98122 13.1506C7.98439 13.1516 7.98809 13.1527 7.99232 13.1537H8.00341C8.00764 13.1537 8.01133 13.1527 8.0145 13.1506C8.01767 13.1495 8.02084 13.1479 8.02401 13.1458L9.30778 12.4041L5.6118 10.2708C5.57535 10.2486 5.54048 10.2217 5.50878 10.1916C5.47807 10.1608 5.45144 10.1261 5.42954 10.0886C5.40893 10.0505 5.3915 10.0109 5.3804 9.9681C5.36931 9.9269 5.36297 9.8841 5.36456 9.83973V6.24042C5.20924 6.29748 5.05867 6.36722 4.91603 6.44963C4.77339 6.53363 4.63867 6.62872 4.51188 6.73491C4.38667 6.8411 4.26939 6.95838 4.1632 7.08517C4.05702 7.21038 3.96351 7.34668 3.88109 7.48932H3.87792ZM14.3905 9.93641C14.4286 9.95859 14.4635 9.98395 14.4951 10.0157C14.5253 10.0458 14.5522 10.0806 14.5744 10.1187C14.595 10.1567 14.6124 10.1979 14.6235 10.2391C14.633 10.2819 14.6394 10.3247 14.6378 10.3691V13.9684C15.1465 13.7814 15.5903 13.4533 15.9184 13.0222C16.248 12.5911 16.4462 12.076 16.4921 11.5372C16.5381 10.9983 16.4303 10.4563 16.1799 9.97603C15.9295 9.4958 15.5475 9.09641 15.0784 8.82698L12.018 7.05823C12.0148 7.05718 12.0111 7.05612 12.0069 7.05506H11.9958C11.9926 7.05612 11.9889 7.05718 11.9847 7.05823C11.9815 7.05929 11.9783 7.06087 11.9752 7.06299L10.6977 7.80155L14.3937 9.93641H14.3905ZM15.6664 8.01868H15.6648V8.02026L15.6664 8.01868ZM15.6648 8.01709C15.7567 7.48457 15.6949 6.93619 15.4857 6.43695C15.2781 5.93771 14.931 5.5082 14.4872 5.19756C14.0435 4.88851 13.5204 4.711 12.98 4.68722C12.438 4.66504 11.9023 4.79658 11.4331 5.06602L8.37269 6.83318C8.36952 6.83529 8.36688 6.83793 8.36477 6.8411L8.35843 6.85061C8.35737 6.85378 8.35631 6.85748 8.35526 6.8617C8.3542 6.86487 8.35367 6.86857 8.35367 6.8728V8.34992L12.0497 6.21507C12.0877 6.19288 12.1289 6.17703 12.1701 6.16435C12.2129 6.15325 12.2557 6.1485 12.2985 6.1485C12.3429 6.1485 12.3857 6.15325 12.4284 6.16435C12.4697 6.17703 12.5093 6.19288 12.5473 6.21507L15.576 7.96479C15.603 7.98064 15.6426 8.00283 15.6648 8.01709ZM7.65632 6.38148C7.65632 6.33869 7.66266 6.2959 7.67375 6.2531C7.68485 6.2119 7.70069 6.17069 7.72288 6.13265C7.74507 6.0962 7.77043 6.06133 7.80213 6.02963C7.83224 5.99952 7.86711 5.97258 7.90515 5.95197L10.9339 4.20383C10.9624 4.1864 11.002 4.16421 11.0242 4.15311C10.609 3.80602 10.1018 3.58414 9.56453 3.51599C9.02725 3.44625 8.48205 3.53183 7.99232 3.76164C7.501 3.99145 7.08575 4.35757 6.79572 4.81402C6.50568 5.27205 6.35195 5.80141 6.35195 6.34344V9.87776C6.353 9.88199 6.35406 9.88569 6.35512 9.88886C6.35617 9.89203 6.35776 9.8952 6.35987 9.89837C6.36198 9.90154 6.36463 9.90471 6.3678 9.90788C6.36991 9.90999 6.37308 9.9121 6.37731 9.91422L7.65632 10.6528V6.38148ZM8.3505 11.0522L9.99721 12.0031L11.6439 11.0522V9.15188L9.9988 8.20094L8.35209 9.15188L8.3505 11.0522Z" />
            </svg>
          </Box>
        );
      } else if (modelName.includes("claude")) {
        return (
          <Box
            as="span"
            w="20px"
            h="20px"
            bg="orange.500"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M0 4C0 1.79086 1.79086 0 4 0H16C18.2091 0 20 1.79086 20 4V16C20 18.2091 18.2091 20 16 20H4C1.79086 20 0 18.2091 0 16V4Z"
                fill="claude"
              ></path>
              <path
                d="M6.04932 3.90689C6.01414 3.99386 5.98566 4.07401 5.98399 4.08424C5.98231 4.09618 5.97226 4.11494 5.96221 4.12688C5.95383 4.14052 5.93876 4.17292 5.93206 4.20191C5.9153 4.26501 5.9153 4.2616 5.8952 4.31276C5.82652 4.47988 5.78967 4.57708 5.78967 4.59755C5.78967 4.60948 5.78464 4.61971 5.77627 4.61971C5.76957 4.61971 5.74946 4.66576 5.73271 4.72203C5.71429 4.77831 5.69586 4.82435 5.68916 4.82435C5.68246 4.82435 5.67743 4.83458 5.67743 4.84482C5.67743 4.86528 5.43956 5.48943 5.40438 5.56617C5.39601 5.58493 5.33235 5.74523 5.26367 5.92428C5.02245 6.55696 4.91524 6.8281 4.89179 6.87756C4.87838 6.90484 4.86833 6.93383 4.86833 6.94236C4.86833 6.95089 4.85158 6.99352 4.83315 7.03786C4.81305 7.08049 4.7963 7.12483 4.79295 7.13506C4.7896 7.15212 4.7829 7.17087 4.7293 7.30048C4.71589 7.33458 4.70752 7.36528 4.71087 7.3704C4.71589 7.37381 4.70752 7.38745 4.69244 7.39939C4.67904 7.41132 4.66731 7.4352 4.66731 7.45225C4.66731 7.47101 4.66061 7.49829 4.65224 7.51535C4.61874 7.57503 4.55005 7.74557 4.55005 7.76433C4.55005 7.77456 4.54 7.80184 4.52828 7.82572C4.51655 7.84959 4.48807 7.9178 4.4663 7.9792C4.44452 8.04059 4.41604 8.1088 4.40432 8.13267C4.39259 8.15655 4.38254 8.18383 4.38254 8.19407C4.38254 8.20259 4.36746 8.24352 4.34904 8.28274C4.33061 8.32026 4.31553 8.36289 4.31553 8.37653C4.31553 8.38847 4.30716 8.40723 4.29711 8.41746C4.28705 8.42769 4.277 8.44645 4.27533 8.45839C4.27198 8.47203 4.2636 8.49761 4.2569 8.51637C4.2502 8.53513 4.13462 8.83015 4.00228 9.17292C3.86994 9.51569 3.72755 9.87892 3.68567 9.98295C3.6438 10.0853 3.60694 10.1825 3.60359 10.1961C3.60192 10.2098 3.58684 10.249 3.57344 10.2814C3.50643 10.4349 3.4277 10.6446 3.4277 10.6668C3.4277 10.6804 3.421 10.6907 3.41095 10.6907C3.40257 10.6907 3.3942 10.706 3.3942 10.7231C3.3942 10.7418 3.38582 10.7623 3.37745 10.7674C3.36739 10.7742 3.36404 10.7862 3.37075 10.7947C3.37577 10.8032 3.37242 10.8203 3.36404 10.8322C3.35567 10.8441 3.29536 10.9908 3.23003 11.1596C3.1647 11.3284 3.10105 11.4887 3.09099 11.5177C3.07927 11.545 3.06922 11.5723 3.06754 11.5774C3.06587 11.5825 3.05917 11.6013 3.05079 11.6201C2.97038 11.8179 2.82465 12.2033 2.82465 12.2203C2.82465 12.2323 2.81627 12.2425 2.80454 12.2425C2.79449 12.2425 2.79114 12.2476 2.79617 12.2544C2.80287 12.2595 2.80119 12.2732 2.79282 12.2834C2.78444 12.2937 2.76936 12.3329 2.75764 12.3704C2.74591 12.4079 2.72414 12.4659 2.71073 12.4983C2.69566 12.5307 2.68393 12.5614 2.68226 12.5665C2.68058 12.5716 2.67388 12.5904 2.66551 12.6091C2.6588 12.6279 2.5918 12.7967 2.51977 12.9843C2.44774 13.1719 2.3824 13.3407 2.37403 13.3595C2.36565 13.3782 2.3422 13.4396 2.3221 13.4959C2.30032 13.5522 2.27352 13.6204 2.26179 13.6494C2.23164 13.721 2.16463 13.9035 2.16296 13.9171C2.16296 13.9239 2.15291 13.9393 2.14286 13.9495C2.13113 13.9615 2.12108 13.9853 2.12108 14.0024C2.12108 14.0194 2.11438 14.0467 2.10433 14.0638C2.09595 14.0791 2.01555 14.2804 1.92676 14.5106C1.83798 14.7408 1.75925 14.9437 1.75087 14.9625C1.74417 14.9812 1.70564 15.0767 1.66879 15.1756C1.63026 15.2746 1.59006 15.3769 1.57833 15.4059C1.56493 15.4332 1.54148 15.4945 1.5264 15.5423C1.50965 15.5883 1.48787 15.6378 1.4795 15.648C1.47112 15.66 1.46945 15.6702 1.47447 15.6702C1.48117 15.6702 1.47447 15.6907 1.46107 15.7162C1.44767 15.7435 1.40914 15.8373 1.37564 15.926C1.34046 16.0147 1.29858 16.1238 1.28183 16.1681L1.25 16.25H2.6119H3.97213L4.03076 16.0914C4.06258 16.0061 4.08939 15.9311 4.08939 15.926C4.09106 15.9209 4.10446 15.8885 4.11954 15.851C4.13462 15.8151 4.14802 15.781 4.14802 15.7776C4.14802 15.7691 4.22172 15.5747 4.25523 15.4911C4.26695 15.4621 4.30548 15.3632 4.34066 15.2694C4.37584 15.1756 4.41269 15.075 4.42442 15.0477C4.43447 15.0188 4.45457 14.9693 4.4663 14.9369C4.47635 14.9045 4.4931 14.8619 4.50147 14.8431C4.5199 14.7988 4.73432 14.2343 4.74102 14.2121C4.74437 14.2019 4.75442 14.1712 4.76615 14.1439C4.7762 14.1149 4.79798 14.0587 4.81305 14.016C4.82813 13.9734 4.84823 13.9291 4.85661 13.9171C4.86498 13.9069 4.86833 13.8967 4.86331 13.8967C4.85828 13.8967 4.88006 13.837 4.91021 13.7636L4.96717 13.6323H7.50838H10.0496L10.1735 13.9563C10.2406 14.1337 10.3009 14.2923 10.3059 14.3059C10.3109 14.3196 10.3377 14.3929 10.3662 14.4679C10.393 14.543 10.4231 14.6197 10.4315 14.6385C10.4382 14.6572 10.4834 14.7766 10.5304 14.9028C10.5773 15.029 10.6258 15.1603 10.6392 15.1927C10.6811 15.295 10.7029 15.3547 10.7046 15.3666C10.7062 15.3718 10.7113 15.3837 10.7196 15.3905C10.7263 15.3973 10.7314 15.4144 10.7314 15.4263C10.7314 15.44 10.7498 15.4928 10.7733 15.544C10.7967 15.5952 10.8151 15.6429 10.8151 15.6497C10.8151 15.6634 10.929 15.9652 10.9743 16.0709C10.986 16.0982 11.0044 16.1511 11.0161 16.1852L11.0363 16.25H12.3931H13.75L13.7399 16.2057C13.7332 16.1818 13.7148 16.1289 13.6981 16.0897C13.6813 16.0522 13.6277 15.9158 13.5791 15.7896C13.5306 15.6634 13.482 15.5355 13.4703 15.5082C13.4585 15.4792 13.3982 15.3257 13.3379 15.1671C13.2759 15.0068 13.2207 14.8619 13.2123 14.8431C13.2039 14.8244 13.1905 14.7902 13.1804 14.7664C13.1704 14.7425 13.0866 14.5242 12.9928 14.2804C12.899 14.0365 12.8153 13.8216 12.8069 13.8029C12.8002 13.7841 12.7767 13.7261 12.7583 13.675C12.7382 13.6238 12.7114 13.5556 12.6963 13.5232C12.6829 13.4925 12.6695 13.4567 12.6662 13.4465C12.6645 13.4362 12.6494 13.397 12.6343 13.3612C12.6176 13.3237 12.6025 13.2793 12.5992 13.2623C12.5958 13.2452 12.5874 13.2214 12.5791 13.2094C12.5724 13.1992 12.5657 13.1855 12.564 13.1804C12.564 13.1753 12.5573 13.1565 12.5489 13.1378C12.5422 13.119 12.5188 13.0611 12.4987 13.0099C12.4786 12.9587 12.4551 12.9008 12.4484 12.882C12.44 12.8632 12.4333 12.8445 12.4317 12.8394C12.43 12.8342 12.4233 12.8155 12.4149 12.7967C12.3027 12.5136 12.1888 12.2135 12.1888 12.1965C12.1888 12.1845 12.1821 12.1743 12.1754 12.1743C12.167 12.1743 12.1603 12.1641 12.1603 12.1521C12.1586 12.1317 12.0765 11.9117 12.0279 11.8042C12.0145 11.7752 12.0045 11.748 12.0045 11.7428C12.0045 11.7394 11.9944 11.7087 11.981 11.678C11.9676 11.6456 11.9576 11.6166 11.9576 11.6115C11.9559 11.6064 11.9325 11.545 11.904 11.4751C11.8336 11.3012 11.8236 11.2773 11.8018 11.2312C11.7934 11.2091 11.7834 11.1818 11.78 11.1681C11.7767 11.1545 11.7666 11.1238 11.7582 11.0999C11.7482 11.0761 11.6979 10.9465 11.6443 10.81C11.5924 10.6736 11.5438 10.5474 11.5354 10.5286C11.4969 10.4417 11.4266 10.2609 11.3813 10.1381C11.3545 10.0631 11.3227 9.97783 11.3093 9.94884C11.2959 9.92156 11.2641 9.8397 11.2373 9.76978C11.2105 9.69986 11.182 9.62653 11.1753 9.60778C11.1669 9.58902 11.1602 9.57026 11.1585 9.56514C11.1485 9.53786 11.1284 9.48329 11.115 9.4543C11.1083 9.43554 11.0781 9.3588 11.0497 9.28377C11.0212 9.20873 10.991 9.13029 10.9826 9.10812C10.9592 9.05525 10.9424 9.01262 10.8855 8.86596C10.8587 8.79604 10.8302 8.72272 10.8235 8.70396C10.8151 8.6852 10.8084 8.66644 10.8068 8.66132C10.8051 8.65621 10.795 8.63063 10.785 8.60505C10.7733 8.57776 10.7649 8.54707 10.7649 8.53513C10.7649 8.5249 10.7582 8.50784 10.7515 8.49761C10.7431 8.48909 10.7263 8.45498 10.7146 8.42258C10.7029 8.39018 10.6811 8.33561 10.6677 8.30321C10.6543 8.2708 10.6376 8.22817 10.6309 8.20941C10.6225 8.19066 10.5471 7.99454 10.4633 7.77456C10.2824 7.30389 10.2925 7.32776 10.2473 7.22544C10.2272 7.18111 10.2121 7.13677 10.2121 7.12654C10.2121 7.1163 10.2054 7.09584 10.197 7.07879C10.1719 7.02933 10.1484 6.97135 10.0948 6.8281C10.0663 6.75307 10.0362 6.67463 10.0278 6.65246C10.0178 6.63199 10.0044 6.59789 9.99598 6.57572C9.9675 6.50239 9.92897 6.40007 9.85359 6.20396C9.81004 6.09482 9.77654 5.99761 9.77654 5.98738C9.77654 5.97715 9.77151 5.96692 9.76481 5.96351C9.75643 5.9601 9.73801 5.92258 9.72293 5.87654C9.70618 5.8322 9.67938 5.76057 9.6643 5.71453C9.64755 5.67019 9.62745 5.62926 9.6174 5.62244C9.60902 5.61562 9.60567 5.6088 9.61237 5.6088C9.61907 5.6088 9.60399 5.56105 9.58222 5.50137C9.54369 5.40246 9.53196 5.37347 9.49176 5.27967C9.48338 5.25921 9.41805 5.09209 9.34937 4.90962C9.27901 4.72715 9.21201 4.55321 9.20028 4.52592C9.18856 4.49693 9.11485 4.31105 9.03779 4.11153L8.89875 3.75H7.50503H6.1113L6.04932 3.90689ZM7.57371 7.09413C7.58041 7.1163 7.59548 7.15723 7.60721 7.18622C7.61894 7.21351 7.66584 7.33629 7.71274 7.45907C7.75797 7.58015 7.80153 7.69611 7.80823 7.71487C7.8166 7.73363 7.82331 7.75239 7.82498 7.7575C7.84006 7.80184 7.90371 7.97067 7.92214 8.0133C7.93219 8.03718 7.94057 8.05935 7.94224 8.06446C7.94392 8.06958 7.95062 8.08493 7.95564 8.09857C7.96067 8.11221 8.00422 8.22817 8.05113 8.35437C8.09803 8.48056 8.14661 8.60675 8.15834 8.63574C8.17174 8.66303 8.19016 8.7176 8.20189 8.75512C8.21362 8.79263 8.22869 8.83015 8.23539 8.84038C8.24377 8.84891 8.24879 8.86085 8.25047 8.86596C8.25047 8.8796 8.27057 8.93929 8.2957 8.99727C8.3091 9.02626 8.31915 9.05525 8.31915 9.06207C8.31915 9.0689 8.3292 9.09789 8.3426 9.12688C8.356 9.15757 8.41296 9.30764 8.46991 9.46282C8.52687 9.61801 8.58718 9.7749 8.60393 9.81242C8.619 9.84993 8.64246 9.90962 8.65418 9.94713C8.66591 9.98295 8.70444 10.087 8.74129 10.1774C8.77814 10.2677 8.80997 10.353 8.81332 10.3666C8.81667 10.3803 8.85017 10.4724 8.8887 10.5713C8.92723 10.6702 8.97246 10.793 8.99089 10.8441C9.00931 10.8953 9.03109 10.9447 9.03947 10.9533C9.04952 10.9635 9.05622 10.9857 9.05622 11.0027C9.05622 11.0198 9.06795 11.0556 9.08135 11.0795C9.1182 11.1511 9.16008 11.2926 9.145 11.3029C9.1383 11.308 8.39118 11.3097 7.48827 11.3046L5.84327 11.296L5.88348 11.1937C5.90358 11.1374 5.96221 10.984 6.01246 10.8527C6.06272 10.7214 6.10795 10.5986 6.11632 10.5798C6.12302 10.5611 6.1314 10.5372 6.13307 10.5286C6.1381 10.5099 6.23358 10.2609 6.25704 10.2046C6.26541 10.1859 6.29724 10.1057 6.32572 10.0256C6.35587 9.94543 6.39105 9.85505 6.40445 9.82265C6.41785 9.79195 6.43125 9.75614 6.43293 9.74591C6.43963 9.70839 6.51333 9.52251 6.53009 9.50205C6.54014 9.49011 6.54349 9.47988 6.53679 9.47988C6.53009 9.47988 6.53679 9.45771 6.55186 9.42872C6.56526 9.40143 6.57699 9.37415 6.57699 9.36903C6.57699 9.36392 6.58872 9.33322 6.60212 9.29911C6.61552 9.26501 6.62724 9.23431 6.62724 9.2309C6.62724 9.2292 6.63562 9.20532 6.64735 9.17804C6.6574 9.15246 6.66745 9.12688 6.66912 9.12176C6.6775 9.08765 6.70263 9.01774 6.72775 8.95975C6.74283 8.92224 6.78303 8.81821 6.81654 8.72954C6.85004 8.64086 6.91035 8.47885 6.95223 8.37142C7.11136 7.95532 7.45477 7.04127 7.49162 6.93895C7.5 6.91508 7.54523 7.00034 7.57371 7.09413Z"
                fill="white"
              ></path>
              <path
                d="M11.25 3.77388C11.25 3.78752 11.2567 3.80287 11.265 3.80798C11.2717 3.8131 11.2884 3.85232 11.3001 3.89495C11.3118 3.93759 11.3285 3.97169 11.3352 3.97169C11.3435 3.97169 11.3502 3.98704 11.3502 4.0058C11.3502 4.02456 11.3569 4.05355 11.3653 4.0706C11.3736 4.08595 11.3937 4.1337 11.4104 4.17633C11.4271 4.21896 11.4521 4.28377 11.4671 4.32128C11.5323 4.47647 11.6242 4.73226 11.6208 4.7425C11.6192 4.75102 11.6242 4.75614 11.6342 4.75614C11.6442 4.75614 11.6509 4.76637 11.6509 4.77831C11.6509 4.79877 11.681 4.87892 11.7561 5.0631C11.7645 5.08697 11.7745 5.11255 11.7778 5.12278C11.7795 5.13131 11.8346 5.27456 11.9014 5.43827C11.9666 5.60198 12.0234 5.74864 12.0267 5.76228C12.0301 5.77592 12.0418 5.81003 12.0535 5.83902C12.0651 5.8663 12.0752 5.89359 12.0768 5.8987C12.0785 5.90382 12.0869 5.92599 12.0986 5.94986C12.1086 5.97374 12.1804 6.16132 12.2606 6.36767C12.3391 6.57401 12.4176 6.77694 12.4343 6.81958C12.4527 6.86221 12.4944 6.96965 12.5278 7.05832C12.618 7.29707 12.6347 7.34141 12.6665 7.41644C12.6815 7.45396 12.8636 7.92463 13.0323 8.36289C13.0657 8.44645 13.1008 8.53854 13.1125 8.56753C13.1242 8.59482 13.1542 8.67156 13.1793 8.73806C13.2027 8.80287 13.2294 8.87278 13.2378 8.89154C13.2461 8.9103 13.2695 8.96828 13.2879 9.01944C13.3079 9.0706 13.333 9.13199 13.343 9.15587C13.353 9.17974 13.3614 9.20191 13.3614 9.20703C13.363 9.21726 13.3964 9.30252 13.4349 9.39461C13.5167 9.59584 13.5885 9.79536 13.5885 9.81583C13.5885 9.82776 13.5969 9.83799 13.6052 9.83799C13.6153 9.83799 13.6219 9.84823 13.6219 9.85846C13.6219 9.88063 13.7038 10.104 13.7489 10.2046C13.7606 10.2319 13.7906 10.3087 13.8157 10.3752C13.8391 10.44 13.8641 10.5014 13.8692 10.5116C13.8758 10.5201 13.8808 10.5406 13.8842 10.5542C13.8859 10.5679 13.8942 10.5935 13.9042 10.6139C13.9143 10.6327 14.0278 10.9243 14.1565 11.2619C14.2867 11.5996 14.407 11.9083 14.4254 11.9475C14.4421 11.9884 14.4571 12.0259 14.4571 12.0344C14.4571 12.043 14.4722 12.0805 14.4905 12.1197C14.5089 12.1572 14.5239 12.2016 14.5239 12.2152C14.5239 12.2289 14.5306 12.2442 14.539 12.2493C14.5473 12.2544 14.559 12.28 14.5657 12.3056C14.5707 12.3329 14.5874 12.3755 14.5991 12.4045C14.6125 12.4318 14.6709 12.5819 14.7311 12.737C14.7895 12.8922 14.9399 13.281 15.0651 13.6033C15.1904 13.9256 15.2923 14.1917 15.2923 14.1968C15.2923 14.2002 15.3073 14.236 15.3241 14.2769C15.3408 14.3162 15.3675 14.3827 15.3842 14.4253C15.4176 14.5157 15.456 14.6095 15.4777 14.6504C15.4861 14.6675 15.4928 14.6913 15.4928 14.7033C15.4928 14.7152 15.5111 14.7664 15.5345 14.8141C15.5579 14.8636 15.5763 14.9079 15.5763 14.9147C15.5763 14.9284 15.6381 15.087 15.6865 15.2046C15.6999 15.2336 15.7099 15.2609 15.7099 15.266C15.7099 15.2694 15.7233 15.3035 15.7383 15.3394C15.7533 15.3769 15.7667 15.4093 15.7684 15.4144C15.77 15.4195 15.7767 15.4383 15.7851 15.457C15.8836 15.706 16.0774 16.2193 16.0774 16.2329C16.0774 16.2449 16.5384 16.25 17.4137 16.25H18.75L18.7183 16.1681C18.7016 16.1238 18.6514 15.9959 18.608 15.8834C18.5646 15.7708 18.5161 15.648 18.4994 15.6105C18.4844 15.573 18.461 15.5133 18.4493 15.4775C18.4376 15.44 18.4193 15.3905 18.4059 15.3666C18.3925 15.3411 18.3825 15.3138 18.3825 15.307C18.3825 15.2984 18.3725 15.2677 18.3591 15.2387C18.3224 15.1552 18.1804 14.7937 18.1771 14.7749C18.1704 14.7476 18.0317 14.3861 18.0134 14.3537C18.0067 14.3383 17.9599 14.2224 17.9131 14.0979C17.8664 13.9734 17.8196 13.8523 17.8112 13.8284C17.8012 13.8046 17.7912 13.7773 17.7895 13.7688C17.7862 13.7585 17.7695 13.7142 17.7494 13.6716C17.7311 13.6289 17.7027 13.5556 17.6876 13.5113C17.6726 13.4652 17.6476 13.397 17.6308 13.3595C17.6008 13.2913 17.5774 13.2299 17.5741 13.2145C17.569 13.1941 17.1565 12.1402 17.1281 12.0771C17.1114 12.0362 17.0963 11.9935 17.0963 11.9799C17.0963 11.9662 17.0896 11.9492 17.0796 11.9441C17.0713 11.9389 17.0629 11.9219 17.0629 11.9083C17.0629 11.8946 17.0479 11.852 17.0312 11.8111C17.0128 11.7718 16.9644 11.6508 16.9243 11.5433C16.7305 11.0385 16.6253 10.764 16.6119 10.7333C16.5885 10.6787 16.5134 10.486 16.2728 9.86357C16.1509 9.54468 16.044 9.26842 16.0356 9.24966C16.0273 9.2309 16.0206 9.21214 16.0189 9.20703C16.0173 9.20191 16.0089 9.17974 15.9989 9.15587C15.9889 9.13199 15.9671 9.07913 15.9521 9.03649C15.9371 8.99386 15.917 8.94441 15.9104 8.92565C15.8636 8.8148 15.6414 8.2384 15.6347 8.21794C15.6314 8.2043 15.6164 8.16166 15.5997 8.12415C15.583 8.08663 15.5696 8.05252 15.5679 8.04741C15.5679 8.03888 15.3675 7.52046 15.3424 7.45907C15.3341 7.44031 15.3107 7.38233 15.2923 7.33117C15.2355 7.18452 15.2305 7.17258 15.2021 7.10607C15.1871 7.07197 15.1754 7.04127 15.1754 7.03615C15.1754 7.03104 15.1687 7.00887 15.1604 6.9867C15.152 6.96453 15.1453 6.94407 15.147 6.93895C15.147 6.93383 15.1303 6.89973 15.1102 6.86221C15.0919 6.82469 15.0752 6.78888 15.0752 6.78206C15.0752 6.77353 14.9983 6.57401 14.9065 6.33697C14.8129 6.10164 14.7261 5.87654 14.7127 5.84072C14.6993 5.80321 14.6843 5.76398 14.6776 5.75546C14.6726 5.74522 14.6659 5.72476 14.6626 5.71112C14.6609 5.69748 14.6509 5.66678 14.6392 5.64291C14.6108 5.58151 14.5974 5.54911 14.5406 5.39563C14.5122 5.3206 14.4822 5.24386 14.4738 5.2251C14.4671 5.20634 14.4104 5.05969 14.3486 4.90109C14.1999 4.51057 14.0896 4.22579 14.0729 4.18486C14.0646 4.1661 14.0429 4.11153 14.0262 4.06549C14.0078 4.01774 13.9727 3.92906 13.9477 3.86426L13.9026 3.75H12.5763C11.4321 3.75 11.25 3.75341 11.25 3.77388Z"
                fill="white"
              ></path>
            </svg>
          </Box>
        );
      } else if (modelName.includes("gemini")) {
        return (
          <Box
            as="span"
            w="20px"
            h="20px"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="transparent"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_8307_27112)">
                <g clipPath="url(#clip1_8307_27112)">
                  <path
                    d="M22 10.024C18.8863 10.2151 15.9499 11.5382 13.7441 13.7441C11.5382 15.9499 10.2151 18.8863 10.024 22H9.976C9.78523 18.8862 8.46224 15.9496 6.2563 13.7437C4.05037 11.5378 1.11382 10.2148 -2 10.024L-2 9.976C1.11382 9.78523 4.05037 8.46224 6.2563 6.2563C8.46224 4.05037 9.78523 1.11382 9.976 -2L10.024 -2C10.2151 1.11371 11.5382 4.05008 13.7441 6.25595C15.9499 8.46182 18.8863 9.78492 22 9.976V10.024Z"
                    fill="url(#paint0_radial_8307_27112)"
                  />
                </g>
              </g>
              <defs>
                <radialGradient
                  id="paint0_radial_8307_27112"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(0.382001 7.7545) rotate(18.6832) scale(25.545 204.632)"
                >
                  <stop offset="0.067" stopColor="#9168C0" />
                  <stop offset="0.343" stopColor="#5684D1" />
                  <stop offset="0.672" stopColor="#1BA1E3" />
                </radialGradient>
                <clipPath id="clip0_8307_27112">
                  <path
                    d="M0 4C0 1.79086 1.79086 0 4 0H16C18.2091 0 20 1.79086 20 4V16C20 18.2091 18.2091 20 16 20H4C1.79086 20 0 18.2091 0 16V4Z"
                    fill="white"
                  />
                </clipPath>
                <clipPath id="clip1_8307_27112">
                  <rect
                    width="24"
                    height="24"
                    fill="white"
                    transform="translate(-2 -2)"
                  />
                </clipPath>
              </defs>
            </svg>
          </Box>
        );
      }
      return null;
    }, []);

    const currentModelValue =
      selectedLLM?.s2s_model || selectedLLM?.model || "";
    const currentModelName =
      modelOptions.find((m) => m.value === currentModelValue)?.name ||
      "Select Model";

    return (
      <HStack spacing={2}>
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            rightIcon={<ChevronDownIcon />}
            colorScheme="primary"
            borderColor="primary.400"
            color="gray.800"
            _hover={{ bg: "primary.50", borderColor: "primary.500" }}
            _active={{ bg: "primary.100", borderColor: "primary.600" }}
            _focusVisible={{
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
          >
            <HStack spacing={2}>
              {getModelIcon(currentModelValue)}
              <Text color="gray.800">{currentModelName}</Text>
            </HStack>
          </MenuButton>

          <MenuList
            maxH="300px"
            overflowY="auto"
            borderColor="primary.200"
            _focusWithin={{
              borderColor: "primary.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
            }}
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "var(--chakra-colors-primary-400) transparent",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-primary-400)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "var(--chakra-colors-primary-500)",
              },
            }}
          >
            {modelOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleModelChange(option.value)}
                _hover={{ bg: "primary.50" }}
                _focus={{ bg: "primary.50" }}
                _active={{ bg: "primary.100" }}
              >
                <HStack spacing={2}>
                  {getModelIcon(option.value)}
                  <Text>{option.name}</Text>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <Popover
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          placement="bottom-start"
          closeOnBlur={false}
        >
          <PopoverTrigger>
            <IconButton
              aria-label="Model settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="outline"
              colorScheme="primary"
              borderColor="primary.400"
              color="primary.600"
              _hover={{ bg: "primary.50", borderColor: "primary.500" }}
              _active={{ bg: "primary.100", borderColor: "primary.600" }}
              _focusVisible={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
              onClick={() => setIsSettingsOpen(true)}
            />
          </PopoverTrigger>

          <PopoverContent
            w="300px"
            borderColor="primary.200"
            _focusWithin={{
              borderColor: "primary.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
            }}
          >
            <PopoverBody>
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" fontWeight="medium">
                  Temperature: {tempTemperature.toFixed(2)}
                </Text>

                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  value={tempTemperature}
                  onChange={setTempTemperature}
                  colorScheme="primary"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>

                <ButtonGroup justifyContent="flex-end" size="sm">
                  <Button
                    variant="ghost"
                    color="black"
                    _hover={{ bg: "gray.100" }}
                    onClick={handleSettingsCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="primary"
                    bg="primary.500"
                    _hover={{ bg: "primary.600" }}
                    onClick={handleTemperatureSave}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    );
  },
);

export default ModelSelect;
